// The dial — X2's clock. Wedges for the primary series, a stepped outline for
// an optional second one.
//
// EVERY POSITION GOES THROUGH `polar()`. It measures CLOCKWISE FROM TWELVE,
// which is how a clock face reads and is NOT the trigonometric convention.
// Placing wedges with one convention and labels with the other puts the labels
// ninety degrees from the slots they name, which looks like a data bug and is
// not one — hence the single shared helper in `geometry.ts`.
//
// A measured zero draws a visible stub rather than nothing, because a wedge of
// radius zero is indistinguishable from a slot the fold never reached — and
// those are different claims. A slot that is `null` (withheld, below the
// disclosure floor) draws nothing at all and is counted in the caller's
// found/measured line instead.

import { Figure } from "./Figure";
import { arcPath, polar, ringPath } from "./geometry";
import { maxOf } from "./scale";
import "./charts.css";

export type RadialProps = {
  label: string;
  /** One value per slot, in slot order. `null` = withheld. */
  values: Array<number | null>;
  /** Slot labels; only those at `labelEvery` are drawn. */
  slotLabels: string[];
  /** Draw every Nth slot label — 24 hour labels collide, 6 do not. */
  labelEvery?: number;
  /** Optional second series on the same scale, drawn as an outline. */
  ring?: Array<number | null>;
  ringLabel?: string;
  size?: number;
  notMeasured?: { reason: string };
};

const STUB = 3;

export function Radial({
  label,
  values,
  slotLabels,
  labelEvery = 1,
  ring,
  ringLabel,
  size = 260,
  notMeasured,
}: RadialProps) {
  const slots = values.length;
  const outer = size / 2 - 22;
  const inner = outer * 0.34;
  // One ceiling across both series so the ring is comparable to the wedges
  // rather than a second chart sharing a circle.
  const top = Math.max(maxOf(values), ring ? maxOf(ring) : 0);
  const radius = (value: number) =>
    inner + (top > 0 ? (value / top) * (outer - inner) : 0);

  return (
    <Figure
      label={label}
      width={size}
      height={size}
      origin={[size / 2, size / 2]}
      notMeasured={notMeasured}
    >
      <circle className="maisight-chart-dial-ground" r={outer} />
      {values.map((value, slot) => {
        if (value === null) return null;
        const from = slot / slots;
        const to = (slot + 1) / slots;
        // A measured zero still shows. See the header.
        const end = value > 0 ? radius(value) : inner + STUB;
        return (
          <path
            key={slot}
            className="maisight-chart-dial-wedge"
            data-empty={value > 0 ? undefined : "yes"}
            d={arcPath(from, to, inner, end)}
          />
        );
      })}
      {ring ? (
        <path
          className="maisight-chart-dial-ring"
          d={ringPath(ring.map((v) => (v === null ? null : radius(v))), slots)}
        >
          {ringLabel ? <title>{ringLabel}</title> : null}
        </path>
      ) : null}
      {slotLabels.map((text, slot) => {
        if (slot % labelEvery !== 0) return null;
        const [x, y] = polar((slot + 0.5) / slots, outer + 12);
        return (
          <text
            key={slot}
            className="maisight-chart-dial-label"
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {text}
          </text>
        );
      })}
    </Figure>
  );
}
